import type { AxiosResponse, CancelToken } from 'axios';
import axios from 'axios';
import { encodeSafeURL } from '@hotjar/common';
import {
  mapDeleteScreenshotV2RequestModelToDto,
  mapGetHeatDataResponseDtoToModel,
  mapGetHeatDataV2RequestModelToDto,
  mapGetScreenshotDataResponseDtoToModel,
  mapGetScreenshotDataV2RequestModelToDto,
  mapTakeScreenshotV2RequestModelToDto,
  mapTrackHeatmapViewedRequestModelToDto,
  mapGetRecordingInfoRequestModelToDto,
  mapGetRecordingInfoResponseDtoToModel,
  mapGetRawHeatDataResponseDtoToModel,
  mapGetRawHeatDataResponseModelToDto,
} from './mapper';

export {
  getPinnedPages,
  addPinnedPage,
  updatePinnedPage,
  removePinnedPage,
  searchPinnedPage,
  trackPinnedPage,
} from './pinnedPagesApi';

const getRootPath = (siteId: number, apiVersion: 1 | 2) => `/api/v${apiVersion}/sites/${siteId}`;

const getData = <T>(response: AxiosResponse<T>): T => response.data;

const rejectFailures = <R extends HeatmapAPI.Response>(response: R): Promise<R> =>
  response.success ? Promise.resolve(response) : Promise.reject(new Error(response.error));

const getDataAndRejectFailures = <T extends HeatmapAPI.Response>(response: AxiosResponse<T>) =>
  rejectFailures(getData(response));

export async function getScreenshotDataV2(
  data: HeatmapAPI.GetScreenshotDataV2Request,
  cancelToken?: CancelToken,
): Promise<HeatmapAPI.GetScreenshotDataResponse> {
  const { siteId, deviceType, url } = data;
  const response = await axios
    .post(
      `${getRootPath(siteId, 2)}/screenshots/${deviceType}/${encodeSafeURL(url)}/search`,
      mapGetScreenshotDataV2RequestModelToDto(data),
      {
        cancelToken,
      },
    )
    .then(getDataAndRejectFailures);

  return mapGetScreenshotDataResponseDtoToModel(response);
}

export function takeScreenshotV2(
  data: HeatmapAPI.TakeScreenshotV2Request & Partial<HeatmapAPI.RetakerExtraParams>,
): Promise<HeatmapAPI.Response> {
  const { siteId, deviceType, url } = data;

  return axios
    .post(
      `${getRootPath(siteId, 2)}/screenshots/${deviceType}/${encodeSafeURL(url)}`,
      mapTakeScreenshotV2RequestModelToDto(data),
    )
    .then(getDataAndRejectFailures);
}

export function deleteScreenshotV2(
  data: HeatmapAPI.DeleteScreenshotV2Request,
): Promise<HeatmapAPI.Response> {
  const { siteId, deviceType, url } = data;

  return axios
    .post(
      `${getRootPath(siteId, 2)}/screenshots/${deviceType}/${encodeSafeURL(url)}/delete`,
      mapDeleteScreenshotV2RequestModelToDto(data),
    )
    .then(getDataAndRejectFailures);
}

export async function getHeatDataV2<T extends HeatmapDataType>(
  data: HeatmapAPI.GetHeatDataV2Request<T>,
  cancelToken?: CancelToken,
): Promise<HeatmapAPI.GetHeatDataResponse<T>> {
  const { siteId } = data;

  const response = await axios
    .post(`${getRootPath(siteId, 2)}/heat/search`, mapGetHeatDataV2RequestModelToDto(data), {
      cancelToken,
    })
    .then(getDataAndRejectFailures);

  return mapGetHeatDataResponseDtoToModel(data.type, response);
}

export function trackHeatmapViewed(data: HeatmapAPI.TrackRequest): Promise<HeatmapAPI.Response> {
  const { siteId } = data;
  return axios
    .get(`${getRootPath(siteId, 1)}/heat/track`, {
      params: mapTrackHeatmapViewedRequestModelToDto(data),
    })
    .then(getDataAndRejectFailures);
}

export async function getRecordingInfo(
  data: HeatmapAPI.GetRecordingInfoRequest,
): Promise<HeatmapAPI.GetRecordingInfoResponse> {
  const response = await axios
    .post(
      `/api/v1/sites/${data.siteId}/heat/recording_target/search`,
      mapGetRecordingInfoRequestModelToDto(data),
    )
    .then(getDataAndRejectFailures);

  return mapGetRecordingInfoResponseDtoToModel(response);
}

export function downloadHeatData(
  data: HeatmapAPI.GetHeatDataV2Request<HeatmapDataType>,
): Promise<Blob> {
  const { siteId } = data;

  return axios({
    url: `${getRootPath(siteId, 1)}/heat/download`,
    method: 'POST',
    data: mapGetHeatDataV2RequestModelToDto(data),
    responseType: 'blob',
  }).then((response) => {
    return response.data;
  });
}

export async function getRawHeatData(
  data: HeatmapAPI.GetRawHeatV2Request,
): Promise<HeatmapAPI.GetRawHeatResponse> {
  const { siteId } = data;

  const response = await axios
    .post(`${getRootPath(siteId, 2)}/heat/raw/search`, mapGetRawHeatDataResponseModelToDto(data))
    .then(getDataAndRejectFailures);

  return mapGetRawHeatDataResponseDtoToModel(response);
}
